<script lang="ts">
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import CommunityLogo from '../lib/ui/CommunityLogo.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    timeAgo,
    parseTs,
    formatDuration,
    durationBetween,
    type AdminUser,
  } from '../lib/admin';
  import { ensureCommunities } from '../lib/community.svelte';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { fetchAllPipelines, type JobRecord, type OnboardingCandidate, type PipelineCommunity, type PipelinePage, type StageCell, type StageMeta } from '@api';

  let me = $state<AdminUser | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let data = $state<PipelinePage | null>(null);
  let nowMs = $state(Date.now());
  let refreshedAt = $state<number | null>(null);

  /* ── Naming ────────────────────────────────────────────────────────────────
     Steps are named for what they DELIVER, not for the function that produces
     them. An operator scanning this grid is asking "does this community have its
     comments?", never "did `membership-tier` run?".

     These are SUB-JOB names now. The fifteen flat stages (`feed`, `roster`,
     `stats`, `classroom`, `plans`…) are gone; a step is one sub-job of one job,
     and the server sends the whole set so a name missing here degrades to itself
     rather than to a blank column. */
  const STEP_LABEL: Record<string, string> = {
    // skool-discovery
    access: 'Access',
    'onboard-enqueue': 'Queue',
    // skool-community-meta
    'members-count': 'Headcount',
    'posts-topics': 'Topics',
    'tier-pricing': 'Pricing',
    // skool-community-feed
    posts: 'Posts',
    comments: 'Comments',
    // skool-community-members
    roles: 'Staff',
    'membership-tier': 'Membership',
    'membership-billing': 'Billing',
    // skool-community-classrooms
    resources: 'Classroom',
    'video-transcripts': 'Transcripts',
    external: 'Documents',
    // skool-community-crm
    'member-stats': 'Member stats',
    'cards-states': 'CRM cards',
    // skool-community-outbox
    'expire-leases': 'Leases',
    reconcile: 'Send recovery',
    // skool-community-moderator-voice
    'voice-profiles': 'Voices',
    // skool-community-assistant
    'chunked-text': 'Chunks',
    summaries: 'Lesson summaries',
    embeddings: 'Embeddings',
    // skool-community-weekly-digest — `embed`, `cluster`, `content-match` and
    // `llm-label` went with the clustering pipeline; `extract` moved here.
    extract: 'Questions',
    digest: 'Weekly digest',
    // skool-user-profile-page
    'profile-meta': 'Profile',
    'skools-owned': 'Owned',
    'skools-joined': 'Joined',
  };
  /* ── The fetch plane's vocabulary ──────────────────────────────────────────
     A walk is not a sub-job and the two arrive in the same `steps` map, keyed by
     name. The server prefixes a walk kind that collides with a sub-job (`walk:`)
     and leaves it bare otherwise, so a reader here strips the prefix before
     looking anything up — the kind is what `scripts/cadence.py` is spelled with
     and what the server's own description names.

     Named for what the walk DELIVERS, the same rule the sub-jobs follow: an
     operator scanning the grid asks "are we seeing joins?", never "did
     `members_pulse` close complete?". The raw kind survives in the hint. */
  const FETCH_PLANE = 'fetch-plane';
  const WALK_PREFIX = 'walk:';
  const walkKind = (s: string) => (s.startsWith(WALK_PREFIX) ? s.slice(WALK_PREFIX.length) : s);
  const WALK_LABEL: Record<string, string> = {
    // community, standing
    feed: 'Feed',
    roster_delta: 'Roster',
    roster_full: 'Roster · full',
    members_pulse: 'Joins',
    cancelling_pulse: 'Cancelling',
    churned_pulse: 'Churned',
    paid_view: 'Paying',
    staff_roles: 'Staff list',
    classroom: 'Classroom',
    plans: 'Pricing',
    // fleet, standing — one pair per scraping identity
    self_profile: 'Our profile',
    groups_list: 'Our communities',
    // demand + reconcile: counted, never columned
    post_comments: 'Post comments',
    comment_reconcile: 'Comment recheck',
    post_images: 'Post images',
  };
  const stageLabel = (s: string) => STEP_LABEL[s] ?? WALK_LABEL[walkKind(s)] ?? walkKind(s);

  /** "every 2 min" / "hourly" / "daily" — a walk's declared interval, said the way an
   *  operator reads it. The cadence bucket cannot: `_walk_cadence` folds 5 and 15
   *  minutes into `fast` and an hour and a day into `daily`. */
  function intervalText(secs: number | null | undefined): string | null {
    if (!secs) return null;
    if (secs < 60) return `every ${secs}s`;
    if (secs < 3600) return `every ${Math.round(secs / 60)} min`;
    if (secs < 86400) {
      const hrs = Math.round(secs / 3600);
      return hrs === 1 ? 'hourly' : `every ${hrs}h`;
    }
    const days = Math.round(secs / 86400);
    return days === 1 ? 'daily' : `every ${days}d`;
  }

  /** The rate to print under a column header, or null for a step nothing fires.
   *
   *  EVERY TIMED COLUMN SAYS IT, now that the grid is ordered by rate — the ordering is
   *  only legible if you can see what it sorted on, and "why is Roster to the right of
   *  Feed" has no answer on the page otherwise. It used to be printed only for `daily`,
   *  as the exception to a band whose label carried the cadence for everything else;
   *  with the fetch plane interleaved through the compute flow, no band label can.
   *
   *  `manual` prints nothing: its band already says "On request · no timer", and seven
   *  columns each repeating it is noise. */
  const CADENCE_SHORT: Record<string, string> = {
    minute: 'every min',
    fast: 'every 15 min',
    daily: 'daily',
  };
  const rateLabel = (st: StageMeta): string | null =>
    st.interval_secs ? intervalText(st.interval_secs) : (CADENCE_SHORT[st.cadence] ?? null);

  /** A flow is what actually runs, so it is what the grid bands by. The label says the
   *  cadence, because that is what an operator is judging the column against. */
  const FLOW_LABEL: Record<string, string> = {
    'skool-automations': 'Automations · every minute',
    'skool-discovery': 'Discovery · every 5 min',
    'skools-fast-sync': 'Sync · every 15 min',
    // The two bands the cut-over adds. `skools-compute` is a real flow with a real timer
    // (`deploy/sideguide-compute.timer`, `*:09/15`) and it is what keeps the rollups
    // moving once the sync timer goes. `fetch-plane` is NOT a flow: the walk registry
    // has no `pipeline_jobs` row at all, so its columns arrive with that pseudo-flow
    // name and the cadence is per walk, adaptive between 5 and 15 minutes — hence "up to
    // 15 min" rather than a number that would be wrong half the time.
    'skools-compute': 'Compute · every 15 min',
    // No cadence in this one, because there is no single answer and the columns now
    // carry their own: the walks under it run at 2 minutes, 5, 15, an hour and a day.
    // It also appears MORE THAN ONCE now that the grid is ordered by rate — the compute
    // flow's quarter-hour falls between the 5-minute feed and the hourly paid view — so
    // the label has to read correctly over four columns or over six.
    'fetch-plane': 'Fetch plane · the walk registry',
  };
  const flowLabel = (f: string | null) => (f ? (FLOW_LABEL[f] ?? f) : 'On request · no timer');

  /** Jobs are named for the thing they produce, same rule. */
  const JOB_LABEL: Record<string, string> = {
    'skool-discovery': 'Discovery',
    'skool-community-meta': 'Community',
    'skool-community-feed': 'Feed',
    'skool-community-members': 'Members',
    'skool-community-classrooms': 'Classroom',
    'skool-community-crm': 'CRM',
    'skool-community-transcripts': 'Transcripts',
    'skool-community-moderator-voice': 'Voices',
    'skool-community-assistant': 'Assistant index',
    'skool-community-weekly-digest': 'Weekly digest',
    'skool-community-outbox': 'Outbox',
    'skool-user-profile-page': 'Profile page',
    // Not a job and deliberately named as one here: the band needs a caption, and
    // "Walk registry" is the honest one. Nothing re-runs it from `pipeline.py`.
    'fetch-plane': 'Walk registry',
  };
  const jobLabel = (j: string) => JOB_LABEL[j] ?? j;


  /* ── Cadence ───────────────────────────────────────────────────────────────
     The one idea this page is built on. "Ran 12 hours ago" is alarming for a
     step that should run every 15 minutes and entirely normal for one that
     self-throttles to a day, so an age means nothing until you know which.

     The server decides it, per step, and the old fast/deep/assistant/mining
     tiers are gone with the deep tier itself: `fast` is what a sync pass runs,
     `daily` is the steps that self-throttle (the classroom crawl and plan ladder,
     over 24h; the live billing refresh, hourly), and `manual` has no timer at all —
     which is why a manual step can never be overdue, however long ago it ran. */
  type Cadence = { label: string; note: string; overdueMs: number | null };
  const CADENCE: Record<string, Cadence> = {
    minute: {
      label: 'Every minute',
      note: 'Outbox recovery — a broken Helper send should not wait for the next sync',
      overdueMs: 15 * 60e3,
    },
    fast: {
      label: 'Every sync',
      note: 'Runs every ~15 minutes — what an owner sees change',
      overdueMs: 45 * 60e3,
    },
    daily: {
      label: 'Once a day',
      note: 'Self-throttled: re-read only when the stored copy is over 24h old',
      overdueMs: 36 * 3600e3,
    },
    manual: {
      label: 'On request',
      note: 'No timer — run it from the CLI when you want it',
      overdueMs: null,
    },
  };

  /** The smallest lateness worth painting amber on a walk. Every close re-arms
   *  `next_due_at`, and a claim is granted on a poll rather than on a timer, so a walk
   *  is routinely a few seconds past due while it waits its turn in the queue. */
  const WALK_LATE_GRACE_MS = 5 * 60e3;

  /** Is this walk late? A FACT, not an inference from a cadence bucket.
   *
   *  The bucket cannot answer it. `StageMeta.cadence` has four values built for sub-jobs,
   *  and the server maps 5- and 15-minute walks onto `fast` (amber at 45 minutes) and
   *  hourly and daily walks onto `daily` (amber at 36 hours). Judged that way the
   *  2-minute join probe — the walk the welcome DM and the `Joined` webhook hang off —
   *  could stop for three quarters of an hour without the page changing colour, and an
   *  hourly `paid_view` for a day and a half. The registry already states when it will
   *  next hand the walk out, so that is what lateness is measured against.
   *
   *  `skipped` is excluded: a disabled walk (blocked community, no membership, the wrong
   *  side of the staff gate) keeps whatever `next_due_at` it had and would otherwise be
   *  permanently amber for a decision somebody made on purpose. */
  function walkOverdue(cell: StageCell | undefined): boolean {
    if (!cell?.next_due_at) return false;
    if (cell.status === 'running' || cell.status === 'skipped') return false;
    const declared = Number(cell.detail?.['interval_secs'] ?? 0) * 1000;
    const grace = Math.max(WALK_LATE_GRACE_MS, declared);
    return nowMs - parseTs(cell.next_due_at).getTime() > grace;
  }

  function isOverdue(cell: StageCell | undefined, cadence: string): boolean {
    // A fetch-plane cell carries `next_due_at`; a ledger cell never does.
    if (cell?.next_due_at) return walkOverdue(cell);
    if (!cell?.ran_at) return false;
    if (cell.status === 'running' || cell.status === 'pending') return false;
    const c = CADENCE[cadence];
    return !!c?.overdueMs && nowMs - parseTs(cell.ran_at).getTime() > c.overdueMs;
  }

  /* ── Status vocabulary ─────────────────────────────────────────────────────
     Seven server statuses, three things an operator can do about them: nothing,
     look at it, act on it. The chip colours collapse to exactly that. */
  /** The pill shape, shared. Size varies by use; everything else is one definition. */
  const PILL = 'inline-flex items-center rounded px-1.5 py-px font-mono tracking-wide';
  const CHIP_BASE = `${PILL} text-[0.62rem]`;
  const PILL_SM = `${PILL} text-[0.55rem]`;
  const CHIP: Record<string, string> = {
    ok: 'bg-success/12 text-success',
    partial: 'bg-warning/16 text-warning',
    failed: 'bg-error/16 text-error',
    blocked: 'bg-error/10 text-error/80',
    running: 'bg-info/16 text-info',
    pending: 'bg-base-content/8 text-base-content/50',
    skipped: 'bg-base-content/10 text-base-content/62',
    interrupted: 'bg-base-content/12 text-base-content/70',
    never: 'bg-base-content/6 text-base-content/40',
  };
  const chipCls = (s: string) => CHIP[s] ?? CHIP['skipped'];
  /** Every cell of a community we have lost access to, drawn as inert.
   *
   *  Those stages really did fail, so the status word stays honest — but the failure
   *  describes OUR membership, not their pipeline, and there is nothing on this page to
   *  do about it. Painting it the same red as a broken sync makes the one colour that
   *  should mean "go and look" mean two different things, and 26 of 66 communities are
   *  in this state. Muted, they are still readable when the toggle reveals them. */
  const MUTED_CHIP = 'bg-base-content/8 text-base-content/45';

  /** A cell the SCHEDULE did not produce.
   *
   *  The grid shows the latest run of each step whoever ran it, so a hand-run
   *  (`pipeline.py job … --mode redo`) takes the cell that is meant to answer "is the
   *  tick healthy". Both facts matter — the data really is that fresh, and the schedule
   *  really has not been observed — so the cell is kept and ringed rather than hidden.
   *  `flow` is stamped by `jobs/flows.py` and `scheduler` by the walk registry — both
   *  are the schedule, and there is no hand-run walk to tell apart from one
   *  (`db/walk_health.py` says why that trigger is a constant). Anything else came from
   *  a terminal or the Helper. A null trigger is a row written before the stamp existed:
   *  unknown, not manual, so it is left unmarked.
   *
   *  `scheduler` was missing here until 2026-09-21, which ringed EVERY cell of the
   *  fetch-plane band — the largest band on the page once the cut-over landed — as
   *  "run by hand, not the schedule", about the only work on the page that can only
   *  ever be the schedule. */
  const SCHEDULED_TRIGGERS = new Set(['flow', 'scheduler']);
  const isManual = (s: StageCell | undefined) =>
    s?.trigger != null && !SCHEDULED_TRIGGERS.has(s.trigger);

  /** How long a STEP took, banded so the expensive ones are findable at a glance:
   *  up to 1s green, up to 5s amber, beyond that red.
   *
   *  These are per-step thresholds and deliberately not applied to whole jobs — a job
   *  is a dozen steps in sequence and legitimately runs for minutes, so the same scale
   *  would paint every job row red and tell you nothing. */
  const DURATION_OK_MS = 1_000;
  const DURATION_WARN_MS = 5_000;
  /** A WALK is measured on its own scale. A sub-job is a database pass and a second is
   *  slow; a walk is a browser epoch — navigate, wait out Skool's render, page through —
   *  and ten to thirty seconds is a healthy one. On the sub-job scale every cell of the
   *  fetch band draws red, which is a page-wide false alarm rather than a signal. */
  const WALK_OK_MS = 20_000;
  const WALK_WARN_MS = 90_000;
  const durationCls = (ms: number, plane = false) => {
    const ok = plane ? WALK_OK_MS : DURATION_OK_MS;
    const warn = plane ? WALK_WARN_MS : DURATION_WARN_MS;
    return ms <= ok
      ? 'bg-success/12 text-success'
      : ms <= warn
        ? 'bg-warning/16 text-warning'
        : 'bg-error/16 text-error';
  };

  /** Plain-language status. `skipped` is the one that reads as a problem and
   *  isn't: a stage that decided it had nothing to do is working correctly. */
  const STATUS_WORD: Record<string, string> = {
    ok: 'ok',
    partial: 'partial',
    failed: 'failed',
    blocked: 'blocked',
    running: 'running',
    pending: 'queued',
    skipped: 'no change',
    interrupted: 'interrupted',
    never: 'never run',
  };

  /** The status word, which is NOT the same word for a walk as for a sub-job.
   *
   *  `skipped` is the one that diverges, and it diverges into the opposite meaning. A
   *  sub-job writes it when it looked and found nothing to do — healthy, hence "no
   *  change". A WALK is `skipped` only when the scheduler has switched it OFF: the
   *  community is blocked, no identity holds a membership there, or it is on the wrong
   *  side of the staff gate (`db/walk_health.py::_status`). Calling that "no change"
   *  reads as a quiet, healthy column and it is the opposite — productivityskool, where
   *  we were demoted, showed five walks reading "no change" while nothing was fetching
   *  them at all. The cell already carries the reason in `error`; the word now says
   *  there is one. */
  const statusWord = (s: StageCell, plane: boolean) =>
    plane && s.status === 'skipped' ? 'off' : (STATUS_WORD[s.status] ?? s.status);

  function cellTitle(s: StageCell, stage: string, cadence: string,
                     plane = isPlane(stage)): string {
    const off = plane && s.status === 'skipped';
    const parts = [`${stageLabel(stage)} — ${statusWord(s, plane)}`];
    if (off) parts.push('the scheduler has switched this walk off — nothing is claiming it');
    const p = stepProgress(s);
    if (p) parts.push(p);
    parts.push(s.ran_at ? `ran ${timeAgo(s.ran_at, nowMs)}` : 'never succeeded');
    if (s.duration_ms != null) parts.push(`took ${formatDuration(s.duration_ms)}`);
    // A walk states when it is next due, so the tooltip says it forwards. That is the
    // question an operator actually has in front of a quiet column, and it is the same
    // fact `walkOverdue` judges lateness on — so a cell that is amber also says by how
    // much, rather than leaving the reader to subtract two timestamps.
    //
    // Not for a walk that is OFF: `next_due_at` is whatever it held when the scheduler
    // disabled it, and nothing will ever claim it. "due 5 hours ago" under a column that
    // is switched off by design is the page inventing an alarm.
    if (s.next_due_at && !off) {
      const due = parseTs(s.next_due_at).getTime();
      parts.push(due > nowMs
        ? `next ${untilText(s.next_due_at)}`
        : `due ${timeAgo(s.next_due_at, nowMs)}`);
    }
    if (isOverdue(s, cadence)) {
      parts.push(s.next_due_at
        ? 'overdue — the scheduler has not handed this walk out'
        : `overdue — ${CADENCE[cadence]?.note}`);
    }
    if (s.mode) parts.push(`mode ${s.mode}`);
    if (isManual(s)) parts.push(`run by hand (${s.trigger}) — not the schedule`);
    if (s.error) parts.push(s.error);
    else if (s.detail) parts.push(JSON.stringify(s.detail));
    return parts.join(' · ');
  }

  /** "412 / 1,024 pages · 40%" — the live bar a running sub-job reports.
   *
   *  Every sub-job builds a `cli_progress.Progress` against a denominator it chose, and
   *  it lands on the ledger row. The server computed it and then dropped it before
   *  sending, so this grid could say "running" and nothing else; on a 44k-member roster
   *  sweep that is the least informative hour of the day. */
  function stepProgress(s: StageCell | undefined): string | null {
    const p = s?.progress;
    if (!p) return null;
    const unit = p.unit ? ` ${p.unit}` : '';
    const done = p.done.toLocaleString();
    if (p.total == null) return `${done}${unit}`;
    return `${done} / ${p.total.toLocaleString()}${unit}${p.pct == null ? '' : ` · ${p.pct}%`}`;
  }

  function duration(startIso: string | null, endIso: string | null): string {
    if (!startIso) return '—';
    const end = endIso ? parseTs(endIso).getTime() : nowMs;
    const secs = Math.round((end - parseTs(startIso).getTime()) / 1000);
    if (secs < 0) return '—';
    if (secs < 60) return `${secs}s`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ${String(secs % 60).padStart(2, '0')}s`;
    return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
  }

  /** How far a running job has got. EVERY job pre-creates one pending row per sub-job
   *  before it starts, so the denominator is always real — the old fallback counting
   *  communities existed for a runner that discovered its work as it went. */
  function progressPct(j: JobRecord): number | null {
    const p = j.progress;
    return p?.total ? Math.min(100, Math.round((p.done / p.total) * 100)) : null;
  }

  /** What to type to re-run this step, for the hint under its header.
   *
   *  A FETCH COLUMN IS NOT A JOB. `pipeline.py job fetch-plane:feed <community>` is what
   *  the generic line produced once the walk columns arrived, and there is no such job
   *  and no such command — the page was telling an operator to run something that does
   *  not exist, on the band that most invites you to try. The registry has no "run it
   *  now" verb at all: a walk is claimed when it is due, and what an operator can
   *  actually do is read or change the cadence. */
  const runHint = (st: StageMeta) =>
    st.flow === FETCH_PLANE
      ? `walk kind ${walkKind(st.name)} · scripts/cadence.py show --community <community>`
      : `pipeline.py job ${st.job}:${st.name}` + (st.scope === 'fleet' ? '' : ' <community>');

  /** "in 4 min" / "now" — the onboarding queue's whole value is when the
   *  scheduler will next act, so it is stated forwards rather than as a date. */
  function untilText(iso: string | null): string {
    if (!iso) return '';
    const ms = parseTs(iso).getTime() - nowMs;
    if (ms <= 0) return 'due now';
    const mins = Math.round(ms / 60e3);
    if (mins < 60) return `in ${mins} min`;
    const hrs = Math.round(mins / 60);
    return hrs < 48 ? `in ${hrs}h` : `in ${Math.round(hrs / 24)}d`;
  }

  /** Why a queued community is not ingested yet, in one phrase.
   *
   *  Was a lookup table translating error strings — "run the posts step", "no longer a
   *  member", "BrowserUnavailable" — raised by code that has since been deleted, so it
   *  matched nothing and fell through to the raw text anyway. The flow records which
   *  STEP failed, which is both shorter and still true next month. */
  function reason(c: OnboardingCandidate): string {
    if (c.blocked) return c.block_reason || 'blocked by hand';
    if (c.running) return 'importing now';
    if (!c.attempts) return 'waiting for its turn';
    return c.last_error?.split('\n')[0]?.slice(0, 80) || 'last attempt failed';
  }

  /* ── Derived views ─────────────────────────────────────────────────────── */
  const stageMetas = $derived(data?.stages ?? []);
  /** Fleet-scoped steps (discovery) are not per-community, so they are not columns. */
  const gridStages = $derived(stageMetas.filter((s) => s.scope === 'community'));
  const cadenceOf = $derived(new Map(gridStages.map((s) => [s.name, s.cadence])));
  /** The columns the WALK REGISTRY fills, by stage name. Three things read differently
   *  for them — an absent cell, a duration, and what the hint tells you to run — and all
   *  three are wrong if a fetch column is treated as a sub-job. */
  const planeStages = $derived(
    new Set(stageMetas.filter((s) => s.flow === FETCH_PLANE).map((s) => s.name)),
  );
  const isPlane = (stage: string) => planeStages.has(stage);
  /** Columns banded by FLOW, because a flow is what actually runs. Left to right they go
   *  fastest first and hand-run last, which is the server's ordering
   *  (`api/v1/staff.py::_by_frequency`). The job each step belongs to is the caption
   *  underneath: that is the unit you re-run, not the unit that schedules.
   *
   *  Contiguous grouping, not a lookup, so the server's ordering survives intact — and
   *  a flow whose steps run at different rates therefore appears more than once, which
   *  is the honest drawing of it rather than a bug. */
  /** Contiguous runs of the same key, order preserved — the server sends the steps in
   *  the order they are meant to be read and regrouping them would lose it. */
  function runsOf<T, K>(items: T[], key: (t: T) => K): { key: K; items: T[] }[] {
    return items.reduce<{ key: K; items: T[] }[]>((acc, item) => {
      const last = acc[acc.length - 1];
      if (last && last.key === key(item)) last.items.push(item);
      else acc.push({ key: key(item), items: [item] });
      return acc;
    }, []);
  }

  /** Two levels of band: the FLOW that runs these steps, and the JOB each belongs to.
   *
   *  BOTH CAN APPEAR TWICE, and that is the band earning its place rather than a mistake.
   *  Ordered by rate, the fetch plane's 2- and 5-minute walks sit to the LEFT of the
   *  compute flow's quarter-hour and its hourly and daily walks to the right, so "Fetch
   *  plane" heads two groups with "Compute" between them. Reading the columns alone you
   *  would think the grid had lost its place; the band is what says it has not.
   *
   *  Keyed by flow AND position for exactly that reason — two bands of the same flow are
   *  two different groups, and keying on the name alone is a duplicate-key crash. */
  const bands = $derived(
    runsOf(gridStages, (s) => s.flow).map((b) => ({
      flow: b.key,
      stages: b.items,
      jobs: runsOf(b.items, (s) => s.job),
    })),
  );
  const orderedStages = $derived(bands.flatMap((b) => b.stages.map((s) => s.name)));

  const bySlug = $derived(new Map((data?.items ?? []).map((c) => [c.slug, c])));
  // Flows above the jobs they are running. The ledger has no parent link — a flow and
  // its job are siblings there — so ordering is what carries the containment.
  const running = $derived(
    (data?.jobs ?? [])
      .filter((j) => j.status === 'running')
      .sort((a, b) => Number(b.kind === 'flow') - Number(a.kind === 'flow')),
  );
  const finished = $derived((data?.jobs ?? []).filter((j) => j.status !== 'running'));
  const queue = $derived(data?.onboarding ?? []);
  const corpus = $derived(data?.corpus ?? []);
  /** Steps that belong to no single community — discovery asks our own profile what we
   *  belong to, housekeeping grants registrations and prunes the ledger. They are not
   *  grid columns, and they were invisible entirely until they started recording
   *  against the ledger's GLOBAL sentinel. These two flows run most often of all.
   *
   *  THE FETCH PLANE'S FLEET WALKS ARE NOT IN HERE. `self_profile` and `groups_list` are
   *  `scope: fleet` stages too, but their cells live in `fetch_plane.fleet` keyed
   *  `"<kind>:<egress id>"` — one pair per scraping identity — and never in
   *  `global_steps`, which is the job ledger's map. Listed here they read "never run"
   *  for ever while they are in fact the two walks that run most often of anything we
   *  do, and the identity that owns each one is exactly what a reader needs when one of
   *  them is locked out. They have their own block. */
  const fleetSteps = $derived(
    stageMetas.filter((s) => s.scope === 'fleet' && s.flow !== FETCH_PLANE),
  );
  const globalSteps = $derived(data?.global_steps ?? {});

  /* ── The fetch plane ───────────────────────────────────────────────────────
     The walk registry writes no ledger row at all, so everything about it that is
     not a grid column arrives in its own block: whether anything is claiming walks,
     the walks that belong to a scraping identity rather than to a community, and the
     demand backlogs, which are counted rather than listed because `post_comments` is
     one row per post whose comments moved. */
  const plane = $derived(data?.fetch_plane ?? null);
  const planeOn = $derived(plane?.enabled ?? false);
  /** `[kind, egress id, cell]`, kind-major. The egress is half the identity of the row:
   *  with two identities provisioned one `self_profile` can be locked out while the
   *  other is fine, which is the failure this block exists to show. */
  const fleetWalks = $derived(
    Object.entries(plane?.fleet ?? {})
      .map(([key, cell]) => {
        const at = key.indexOf(':');
        return at < 0
          ? { kind: key, egress: '', cell }
          : { kind: key.slice(0, at), egress: key.slice(at + 1), cell };
      })
      .sort((a, b) => a.kind.localeCompare(b.kind) || a.egress.localeCompare(b.egress)),
  );
  const demandKinds = $derived(
    Object.entries(plane?.demand ?? {}).sort(([a], [b]) => a.localeCompare(b)),
  );

  let showLostAccess = $state(false);
  const visibleCommunities = $derived(
    (data?.items ?? []).filter((c) => showLostAccess || !c.no_access),
  );
  const lostAccessCount = $derived(data?.lost_access_count ?? 0);
  const failedCount = $derived(data?.failed_count ?? 0);

  /** Fleet-wide overdue count — a stage that has not run inside its own cadence.
   *  Distinct from `failed`: nothing errored, the work simply is not happening,
   *  which is exactly the silent condition this page exists to surface.
   *
   *  Lost-access communities are excluded whether or not the toggle is showing them.
   *  Every stage of a community we are not a member of is overdue by definition and
   *  will stay that way for ever, so counting them turns a number meant to say "go and
   *  look" into a constant that means nothing — 26 of 66 on prod. */
  const overdueCount = $derived(
    visibleCommunities.filter(
      (c) =>
        !c.no_access &&
        orderedStages.some((st) => isOverdue(c.steps[st], cadenceOf.get(st) ?? '')),
    ).length,
  );

  async function load(silent = false): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    if (!silent) {
      loading = true;
      error = null;
    }
    let pageErr: unknown = null;
    const pending = fetchAllPipelines().catch((err: unknown) => {
      pageErr = err;
      return null;
    });
    try {
      if (!me) {
        me = asAdminUser(await ensureBootstrap());
        if (redirectIfAwaitingOnboarding(me, navigate)) return;
        if (me.role !== 'superadmin') {
          navigate('/admin');
          return;
        }
        await ensureCommunities();
      }
      const loaded = await pending;
      if (loaded) {
        data = loaded;
        refreshedAt = Date.now();
      } else if (pageErr) throw pageErr;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      if (!silent) error = err instanceof Error ? err.message : 'Could not load pipeline health.';
    } finally {
      loading = false;
    }
  }
  void load();

  // The clock ticks every second and the data refetches every 5s, because this page is
  // watched WHILE a pass runs: at 30s a step counter moving through 478 modules looked
  // frozen, which is indistinguishable from wedged — the one thing the page exists to
  // tell you. Silent — a background refetch must never flash the loading state.
  $effect(() => {
    const tick = setInterval(() => (nowMs = Date.now()), 1_000);
    const poll = setInterval(() => void load(true), 5_000);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  });

  // Manual refresh, for when you have just started something and do not want to wait
  // out the interval. Guarded against double-firing: `load` is already in flight while
  // `loading` is true.
  let refreshing = $state(false);
  async function refreshNow() {
    if (refreshing) return;
    refreshing = true;
    try {
      await load(true);
    } finally {
      refreshing = false;
    }
  }
</script>

{#snippet hint(text: string, run: string)}
  <!-- CSS-only, and focusable so it is reachable without a mouse. `group-focus-within`
       is what makes that work; a hover-only tooltip hides its content from the keyboard
       entirely. -->
  <span class="group relative inline-flex align-middle">
    <button
      type="button"
      class="grid h-3.5 w-3.5 place-items-center rounded-full border border-base-content/25 font-mono text-[0.55rem] leading-none text-base-content/45 transition-colors hover:border-primary hover:text-primary focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary"
      aria-label={text}
    >i</button>
    <span
      role="tooltip"
      class="pointer-events-none absolute left-1/2 top-full z-30 mt-1.5 w-56 -translate-x-1/2 whitespace-normal rounded-box border hair bg-base-100 px-2.5 py-2 text-left normal-case tracking-normal opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none"
    >
      <!-- `whitespace-normal` undoes the header's `whitespace-nowrap`, which a tooltip
           inside a table cell inherits — that is what pushed the text out of the box.
           `break-words` is for the command, which is one unbreakable token wider than
           the tooltip on the longest job name. -->
      <span class="block text-[0.7rem] font-normal leading-snug text-base-content/85">{text}</span>
      <span class="mt-1 block break-words font-mono text-[0.55rem] leading-snug text-base-content/45"
        >{run}</span
      >
    </span>
  </span>
{/snippet}

{#snippet logo(c: PipelineCommunity | undefined, box: string)}
  <CommunityLogo name={c?.name} logoUrl={c?.logo_url} size={box} fallback="·" />
{/snippet}

{#snippet cell(s: StageCell | undefined, stage: string, muted = false)}
  {@const cadence = cadenceOf.get(stage) ?? ''}
  {@const late = !muted && isOverdue(s, cadence)}
  {@const prog = stepProgress(s)}
  {@const plane = isPlane(stage)}
  {#if s}
    <span
      class="{CHIP_BASE} {muted ? MUTED_CHIP : late ? 'bg-warning/16 text-warning' : chipCls(s.status)}
             {isManual(s) ? 'ring-1 ring-base-content/30 ring-offset-1 ring-offset-base-100' : ''}"
      title={cellTitle(s, stage, cadence, plane)}
    >
      {statusWord(s, plane)}
    </span>
    {#if prog}
      <!-- A running step reports its own 0-100%. The bar is drawn only when the
           denominator is known; a step that has counted 412 things without yet knowing
           how many there are shows the count and no bar, which is the honest rendering
           of what it told us. -->
      {#if s.progress?.pct != null}
        <div class="mx-auto mt-1 h-1 w-12 overflow-hidden rounded-full bg-info/18">
          <div class="h-full rounded-full bg-info transition-[width] duration-500"
               style="width: {Math.max(2, s.progress.pct)}%"></div>
        </div>
      {/if}
      <div class="mt-0.5 whitespace-nowrap font-mono text-[0.55rem] text-info">{prog}</div>
    {:else}
      <!-- A walk the registry holds but nothing has closed yet has no "ran" to show, and
           `—` under a `queued` chip tells a reader nothing. What it does have is the
           instant the scheduler will next hand it out, which is the fact that says
           whether it is waiting its turn or stuck. -->
      <div class="mt-0.5 font-mono text-[0.55rem] {late ? 'text-warning' : 'text-base-content/55'}">
        {#if plane && s.status === 'skipped'}
          <!-- Switched off. Its `next_due_at` is a leftover nothing will act on, and its
               `last_ok_at` is from before it was disabled — so the only honest second
               line is why, which the registry put in `error` (the block reason, or
               "disabled"). -->
          {s.error ?? 'disabled'}
        {:else if !s.ran_at && s.next_due_at}
          {parseTs(s.next_due_at).getTime() > nowMs ? untilText(s.next_due_at) : `due ${timeAgo(s.next_due_at, nowMs)}`}
        {:else}
          {timeAgo(s.ran_at, nowMs)}
        {/if}
      </div>
      <!-- How long it took, under how long ago it ran. Its own line rather than
           appended: these are fixed-width grid columns, and `12 minutes ago · 1.2s`
           on one line widens every column in the table to fit the worst cell. -->
      {#if s.duration_ms != null}
        <div class="mt-0.5">
          <span class="{PILL_SM} {muted ? MUTED_CHIP : durationCls(s.duration_ms, plane)}"
                title="took {formatDuration(s.duration_ms)}"
            >{formatDuration(s.duration_ms)}</span>
        </div>
      {/if}
    {/if}
  {:else if plane}
    <!-- A fetch column with no cell is a walk that was never REGISTERED here, which is
         not the same fact as a step that has never run and does not want the same word.
         The scheduler registers a kind per community on purpose: the membership tabs are
         staff-gated, `staff_roles` is the mirror image and only exists where we are an
         ordinary member, and a community no identity holds a membership in gets nothing
         at all. On a fleet where we are staff of eight of a hundred and forty-one, six of
         these columns are legitimately empty for almost every row — "never run" on all of
         them reads as a hundred and thirty-three broken communities. -->
    <span class="{CHIP_BASE} {CHIP['never']}"
          title="{stageLabel(stage)} — no walk registered for this community. Staff-gated
kinds exist only where an egress holds a staff role; `staff_roles` only where it does
not; a community no identity is a member of has none of them."
      >not here</span
    >
  {:else}
    <span class="{CHIP_BASE} {CHIP['never']}" title="{stageLabel(stage)} — no run recorded yet"
      >never run</span
    >
  {/if}
{/snippet}

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error}
    <StateBlock state="error" message={error} onretry={() => load()} />
  {:else if data}
    <PageContainer>
      <!-- Header ------------------------------------------------------------ -->
      <!-- "Pipeline" under an eyebrow reading "Superadmin"; the nav calls this page
           "Pipeline health" and its section "Super admin". Both now come from there.
           This page's own "· updated Xm ago" was the same stamp the header component
           renders, so it hands `refreshedAt` over and keeps only what is genuinely
           its own: the live/idle pill and the manual refresh. -->
      <PageHeader
        section="Super admin"
        title="Pipeline health"
        updated={refreshedAt ? new Date(refreshedAt).toISOString() : null}
        updatedLabel="Polled"
      >
        {#snippet actions()}
          <div class="flex items-center gap-2 font-mono text-[0.65rem] text-base-content/55">
            {#if running.length}
              <span class="relative flex h-2 w-2">
                <span
                  class="absolute inline-flex h-full w-full animate-ping rounded-full bg-info opacity-70"
                ></span>
                <span class="relative inline-flex h-2 w-2 rounded-full bg-info"></span>
              </span>
              {running.length} running
            {:else}
              <span class="inline-flex h-2 w-2 rounded-full bg-base-content/25"></span> idle
            {/if}
            <button
              type="button"
              class="ml-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-base-content/62 transition-colors hover:bg-base-content/8 hover:text-base-content disabled:opacity-45"
              onclick={refreshNow}
              disabled={refreshing}
              title="Refresh now"
              aria-label="Refresh now"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                   stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5"
                   class:animate-spin={refreshing} aria-hidden="true">
                <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                <polyline points="21 3 21 9 15 9" />
              </svg>
            </button>
          </div>
        {/snippet}
      </PageHeader>

      <!-- Running now ------------------------------------------------------- -->
      <section>
        <h2 class="eyebrow">Running now</h2>
        <div class="mt-2 overflow-hidden rounded-box border hair bg-base-100">
          {#if running.length === 0}
            <!-- What this sentence names has to follow the cut-over, or the page
                 explains a quiet panel with a flow whose timer is retired. With the
                 scheduler on the fetching is the walk registry's and never a job, so
                 "nothing running" here is the NORMAL state and the fetch plane above is
                 where an operator should be looking. -->
            <p class="px-4 py-4 text-sm text-base-content/58">
              {#if planeOn}
                No flow running — with the fetch plane on, the scraping is workers claiming
                walks rather than a job, and it does not show here. Compute rolls up every
                15 minutes, automations run every minute and discovery checks for new
                communities every 5.
              {:else}
                Nothing running. The sync flow sweeps every community about every 15
                minutes; discovery checks for new ones every 5.
              {/if}
            </p>
          {:else}
            <!-- Flows first, then the jobs running inside them, indented. A pass and its
                 current job are both open rows; flat and unordered they read as two
                 unrelated jobs instead of one containing the other. -->
            {#each running as j (j.id)}
              <div
                class="flex flex-wrap items-center gap-x-4 gap-y-1 border-b hair py-3 last:border-0"
                class:px-4={j.kind === 'flow'}
                class:pl-10={j.kind !== 'flow'}
                class:pr-4={j.kind !== 'flow'}
              >
                <span class="relative flex h-2 w-2 shrink-0">
                  <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-info opacity-70"></span>
                  <span class="relative inline-flex h-2 w-2 rounded-full bg-info"></span>
                </span>
                <span class="font-medium" class:opacity-80={j.kind !== 'flow'}>{j.type}</span>
                {#if j.kind === 'flow'}
                  <span class="rounded-sm bg-info/12 px-1.5 py-0.5 font-mono text-[0.6rem] uppercase tracking-wide text-info">flow</span>
                {/if}
                <span class="inline-flex items-center gap-1.5 font-mono text-xs text-base-content/72">
                  {#if j.slug}{@render logo(bySlug.get(j.slug), 'h-4 w-4')}{/if}
                  {j.slug ?? 'whole fleet'}
                </span>
                <span class="font-mono text-xs tabular-nums text-info">{duration(j.started_at, null)}</span>
                {#if j.progress}
                  {@const pct = progressPct(j)}
                  <span class="flex min-w-[11rem] items-center gap-2">
                    {#if pct !== null}
                      <span class="h-1 flex-1 overflow-hidden rounded-full bg-base-content/10">
                        <span class="block h-full rounded-full bg-info transition-[width] duration-500" style="width:{pct}%"></span>
                      </span>
                    {/if}
                    {#if pct !== null}
                      <span class="whitespace-nowrap font-mono text-[0.65rem] text-base-content/62"
                        >{j.progress?.done} of {j.progress?.total}
                        {j.progress?.unit ?? 'steps'} · {pct}%</span
                      >
                    {/if}
                  </span>
                {/if}
                <!-- The step in flight. The counter above only moves when a whole step
                     ends, so on a long one this is the only thing that shows the job is
                     alive rather than wedged. -->
                {#if j.step}
                  <span class="whitespace-nowrap font-mono text-[0.65rem] text-base-content/62">
                    <span class="text-base-content/80">{j.step.stage}</span>
                    {#if j.step.done !== undefined}
                      · {j.step.done}{#if j.step.total} / {j.step.total}{/if}
                      {#if j.step.unit}&nbsp;{j.step.unit}{/if}
                      {#if j.step.pct != null}&nbsp;· {j.step.pct}%{/if}
                    {/if}
                  </span>
                {/if}
                <span class="ml-auto font-mono text-[0.65rem] text-base-content/50"
                  >started {timeAgo(j.started_at, nowMs)} · {j.trigger}</span
                >
                {#if durationBetween(j.started_at, j.finished_at) != null}
                  <span class="{PILL_SM} bg-base-content/8 text-base-content/62"
                        title="whole job, start to finish"
                    >{formatDuration(durationBetween(j.started_at, j.finished_at))}</span>
                {/if}
              </div>
            {/each}
          {/if}
        </div>
      </section>

      <!-- Fetch plane -------------------------------------------------------- -->
      <!-- The walk registry writes no ledger row, so none of this reaches the grid:
           whether anything is claiming walks at all, the two walks that belong to a
           scraping identity rather than to a community, and the demand backlogs, which
           are counted because `post_comments` is one row per post whose comments moved —
           92k of them on the largest community. The server has sent all three since the
           cut-over and the page read none of it. -->
      {#if plane}
        <section class="mt-7">
          <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h2 class="eyebrow">Fetch plane</h2>
            <span class="{CHIP_BASE} {planeOn ? 'bg-success/12 text-success' : 'bg-warning/16 text-warning'}"
                  title={planeOn
                    ? 'The scheduler is on: workers claim these walks and fetch their pages.'
                    : 'The scheduler is OFF. Nothing claims a walk, so every row below is inert — a registry full of overdue walks and a registry nothing is driving look identical without this.'}
              >{planeOn ? 'scheduler on' : 'scheduler off'}</span
            >
          </div>
          <p class="mt-1 max-w-2xl text-xs text-base-content/62">
            Every Skool page we fetch is claimed from the walk registry by a worker, not run
            by a flow — so these have no ledger row and no column in the grid.
            {#if !planeOn}
              <span class="text-warning"
                >With the scheduler off nothing is claiming them, whatever they say below.</span
              >
            {/if}
          </p>

          {#if fleetWalks.length}
            <h3 class="mt-3 font-mono text-[0.6rem] uppercase tracking-wide text-base-content/50">
              Per scraping identity
            </h3>
            <div class="mt-1.5 grid gap-px overflow-hidden rounded-box border hair bg-base-300 sm:grid-cols-2 lg:grid-cols-4">
              {#each fleetWalks as w (w.kind + w.egress)}
                {@const late = walkOverdue(w.cell)}
                <div class="bg-base-100 px-4 py-3">
                  <div class="flex items-baseline justify-between gap-2">
                    <!-- privacy-ok: a walk KIND, not a person -->
                    <span class="text-sm font-medium">{stageLabel(w.kind)}</span>
                    <span class="{CHIP_BASE} {late ? 'bg-warning/16 text-warning' : chipCls(w.cell.status)}"
                          title={cellTitle(w.cell, w.kind, '', true)}
                      >{late ? 'overdue' : statusWord(w.cell, true)}</span
                    >
                  </div>
                  <!-- The identity is half the row: one `self_profile` can be locked out
                       while the other is fine, and the id is what `scripts/cadence.py`
                       and the egress budget are spelled with. -->
                  <div class="mt-1 truncate font-mono text-[0.6rem] text-base-content/45"
                       title={w.egress}>{w.egress || 'no egress'}</div>
                  <div class="mt-1 font-mono text-[0.6rem] {late ? 'text-warning' : 'text-base-content/55'}">
                    {w.cell.ran_at ? `ok ${timeAgo(w.cell.ran_at, nowMs)}` : 'never succeeded'}
                    {#if w.cell.next_due_at} · next {untilText(w.cell.next_due_at)}{/if}
                  </div>
                  {#if w.cell.error}
                    <div class="mt-1 break-words font-mono text-[0.6rem] text-error/85">{w.cell.error}</div>
                  {/if}
                </div>
              {/each}
            </div>
          {/if}

          <h3 class="mt-4 font-mono text-[0.6rem] uppercase tracking-wide text-base-content/50">
            Raised by evidence · fleet-wide backlog
          </h3>
          <p class="mt-1 max-w-2xl text-xs text-base-content/62">
            These walks are not on a clock — a feed sweep raises one per post whose comments
            moved, and the row is deleted when it closes. So the question is how much is
            outstanding and how old the front of the queue is, never which rows they are.
          </p>
          {#if demandKinds.length === 0}
            <p class="mt-2 rounded-box border hair bg-base-100 px-4 py-4 text-sm text-base-content/58">
              Nothing outstanding — every demand walk raised so far has closed.
            </p>
          {:else}
            <div class="mt-1.5 grid gap-px overflow-hidden rounded-box border hair bg-base-300 sm:grid-cols-2 lg:grid-cols-3">
              {#each demandKinds as [kind, d] (kind)}
                {@const stale = !!d.oldest_due_at && parseTs(d.oldest_due_at).getTime() < nowMs - 3600e3}
                <div class="bg-base-100 px-4 py-3">
                  <div class="flex items-baseline justify-between gap-2">
                    <!-- privacy-ok: a walk KIND, not a person -->
                    <span class="text-sm font-medium">{stageLabel(kind)}</span>
                    {#if d.backed_off > 0}
                      <span class="{CHIP_BASE} bg-warning/16 text-warning"
                            title="serving a back-off after a failure — they are not being retried right now"
                        >{d.backed_off.toLocaleString()} backed off</span
                      >
                    {/if}
                  </div>
                  <div class="mt-1.5 font-display text-2xl font-bold tabular-nums {d.open > 0 ? 'text-base-content' : 'text-base-content/35'}">
                    {d.open.toLocaleString()}
                  </div>
                  <div class="font-mono text-[0.6rem] {stale ? 'text-warning' : 'text-base-content/55'}">
                    <!-- Stated backwards once it is late: "due now" is what a queue that
                         is one second behind and one that is six hours behind both say,
                         and the difference between those two is the whole signal. -->
                    outstanding{#if d.oldest_due_at} · front of the queue {parseTs(d.oldest_due_at).getTime() > nowMs ? untilText(d.oldest_due_at) : `due ${timeAgo(d.oldest_due_at, nowMs)}`}{/if}
                  </div>
                </div>
              {/each}
            </div>
          {/if}
        </section>
      {/if}

      <!-- Waiting to onboard ------------------------------------------------ -->
      <section class="mt-7">
        <div class="flex items-baseline gap-2">
          <h2 class="eyebrow">Waiting to onboard</h2>
          {#if queue.length}<span class="font-mono text-[0.65rem] text-base-content/50">{queue.length}</span>{/if}
        </div>
        <div class="mt-2 overflow-hidden rounded-box border hair bg-base-100">
          {#if queue.length === 0}
            <p class="px-4 py-4 text-sm text-base-content/58">
              Nothing waiting. A community the scraper account joins is picked up within about five
              minutes.
            </p>
          {:else}
            {#each queue as c (c.slug)}
              <div class="flex flex-wrap items-center gap-x-4 gap-y-1 border-b hair px-4 py-2.5 last:border-0 {c.parked ? 'bg-base-200/40' : ''}">
                <a
                  href="https://www.skool.com/{c.slug}"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="min-w-[13rem] font-mono text-sm hover:text-primary hover:underline">{c.slug}</a
                >
                {#if c.blocked}
                  <span class="{CHIP_BASE} bg-base-content/12 text-base-content/72" title="Someone decided not to onboard this community">
                    blocked
                  </span>
                {:else if c.running}
                  <span class="{CHIP_BASE} bg-info/16 text-info">importing</span>
                {:else if c.parked}
                  <span class="{CHIP_BASE} bg-error/12 text-error" title="The retry schedule gave up — {c.attempts} attempts over four days all failed">
                    gave up
                  </span>
                {:else}
                  <span class="{CHIP_BASE} bg-info/12 text-info">{untilText(c.next_attempt_at)}</span>
                {/if}
                <span class="text-xs text-base-content/72">{reason(c)}</span>
                <span class="ml-auto font-mono text-[0.65rem] text-base-content/50">
                  <!-- Size, because it is what decides the order: the queue is taken
                       smallest-first so a 3-member community is never stuck behind a
                       45,772-member one. -->
                  {#if c.total_members != null}{c.total_members.toLocaleString()} members · {/if}
                  {c.source === 'registration' ? 'owner registered' : 'discovered'}
                  {#if c.attempts} · {c.attempts} {c.attempts === 1 ? 'try' : 'tries'}{/if}
                  {#if c.last_attempt_at} · last {timeAgo(c.last_attempt_at, nowMs)}{/if}
                </span>
              </div>
            {/each}
            {#if queue.some((c) => c.parked || c.blocked)}
              <p class="border-t hair bg-base-200/30 px-4 py-2 text-xs text-base-content/62">
                <span class="font-medium">Blocked</span> means someone decided not to onboard it;
                <span class="font-medium">gave up</span> means the retry schedule stopped after four
                days of failures. Neither is retried. Start one by hand with
                <code class="rounded bg-base-content/8 px-1 font-mono text-[0.65rem]">pipeline.py flow onboarding &lt;community&gt;</code>.
              </p>
            {/if}
          {/if}
        </div>
      </section>

      <!-- Fleet-wide steps --------------------------------------------------- -->
      {#if fleetSteps.length}
        <section class="mt-7">
          <h2 class="eyebrow">Fleet</h2>
          <p class="mt-1 max-w-2xl text-xs text-base-content/62">
            Work that belongs to no single community, so it has no column in the grid below:
            finding communities we have joined, granting a pending registration, keeping the
            ledger bounded. These run more often than anything else.
          </p>
          <div class="mt-2 grid gap-px overflow-hidden rounded-box border hair bg-base-300 sm:grid-cols-2 lg:grid-cols-4">
            {#each fleetSteps as st (st.job + st.name)}
              <!-- privacy-ok: a pipeline STAGE key, not a person -->
              {@const cellData = globalSteps[st.name]}
              <div class="bg-base-100 px-4 py-3">
                <div class="flex items-baseline justify-between gap-2">
                  <span class="inline-flex items-center gap-1.5 text-sm font-medium">
                    <!-- privacy-ok: a pipeline STAGE key, not a person -->
                    {stageLabel(st.name)}
                    {@render hint(st.description, runHint(st))}
                  </span>
                  <span
                    class="{CHIP_BASE} {cellData ? chipCls(cellData.status) : CHIP['never']}"
                    title={cellData ? cellTitle(cellData, st.name, st.cadence) : 'no run recorded yet'}
                  >
                    {cellData ? (STATUS_WORD[cellData.status] ?? cellData.status) : 'never run'}
                  </span>
                </div>
                <div class="mt-1 font-mono text-[0.6rem] text-base-content/55">
                  {jobLabel(st.job)} · {CADENCE[st.cadence]?.label ?? st.cadence}
                </div>
                {#if cellData}
                  {@const prog = stepProgress(cellData)}
                  <div class="mt-1 font-mono text-[0.6rem] {prog ? 'text-info' : 'text-base-content/55'}">
                    {prog ?? `ran ${timeAgo(cellData.ran_at, nowMs)}`}
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        </section>
      {/if}

      <!-- Shared corpus ----------------------------------------------------- -->
      <section class="mt-7">
        <h2 class="eyebrow">Backlogs</h2>
        <p class="mt-1 max-w-2xl text-xs text-base-content/62">
          These steps work off a queue — transcripts, documents, chunks and vectors are fetched for
          whatever is still missing. They run per community now, so when one last ran says nothing
          about whether the fleet is keeping up. The number waiting does.
        </p>
        <div class="mt-2 grid gap-px overflow-hidden rounded-box border hair bg-base-300 sm:grid-cols-2 lg:grid-cols-3">
          {#each corpus as st (st.name)}
            <div class="bg-base-100 px-4 py-3">
              <div class="flex items-baseline justify-between gap-2">
                <!-- privacy-ok: a pipeline STAGE key, not a person -->
                <span class="text-sm font-medium">{stageLabel(st.name)}</span>
                <span class="{CHIP_BASE} {st.cell ? chipCls(st.cell.status) : CHIP['never']}">
                  {st.cell ? (STATUS_WORD[st.cell.status] ?? st.cell.status) : 'never run'}
                </span>
              </div>
              <div class="mt-1.5 font-display text-2xl font-bold tabular-nums {st.waiting > 0 ? 'text-base-content' : 'text-base-content/35'}">
                {st.waiting.toLocaleString()}
              </div>
              <div class="font-mono text-[0.6rem] text-base-content/55">
                waiting{#if st.cell} · ran {timeAgo(st.cell.ran_at, nowMs)}{/if}
              </div>
            </div>
          {/each}
        </div>
      </section>

      <!-- Fleet grid -------------------------------------------------------- -->
      <section class="mt-7">
        <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 class="eyebrow">Every community</h2>
          <div class="flex flex-wrap items-center gap-3 text-xs">
            {#if failedCount > 0}
              <span class="text-error">{failedCount} with a failed stage</span>
            {/if}
            {#if overdueCount > 0}
              <span class="text-warning">{overdueCount} overdue for their cadence</span>
            {/if}
            {#if lostAccessCount > 0}
              <label class="flex cursor-pointer select-none items-center gap-2 text-base-content/72">
                <input type="checkbox" class="toggle toggle-xs" bind:checked={showLostAccess} />
                Show {lostAccessCount} we've lost access to
              </label>
            {/if}
          </div>
        </div>

        <div class="mt-2 overflow-x-auto rounded-box border hair bg-base-100">
          <table class="w-full min-w-[64rem] border-collapse text-sm">
            <thead>
              <!-- Job bands: the grouping IS the explanation, and it is the unit you
                   re-run (`pipeline.py job <name> <slug>`). -->
              <tr class="border-b hair bg-base-200/60 text-left">
                <th class="sticky left-0 z-10 bg-base-200/60 px-3 py-2"></th>
                {#each bands as b, bi (`${b.flow ?? 'manual'}-${bi}`)}
                  <th
                    colspan={b.stages.length}
                    class="border-l hair px-2 py-1.5 text-center"
                    title={b.flow
                      ? `${b.flow} — ${b.stages.length} step${b.stages.length === 1 ? '' : 's'} at this rate. The grid runs fastest-first, so a flow whose steps run at different rates heads more than one group.`
                      : 'No timer fires these — run them from the CLI when you want them'}
                  >
                    <span class="eyebrow text-[0.6rem] {b.flow ? '' : 'text-base-content/45'}"
                      >{flowLabel(b.flow)}</span
                    >
                  </th>
                {/each}
              </tr>
              <!-- Job runs. What you re-run is a job (`pipeline.py job <job>
                   <community>`), so it gets a band of its own rather than a caption
                   repeated under every column. -->
              <tr class="border-b hair bg-base-200/50 text-left">
                <th class="sticky left-0 z-10 bg-base-200/50 px-3 py-1"></th>
                {#each bands as b, bi (`${b.flow ?? 'manual'}-${bi}`)}
                  {#each b.jobs as jb, ji (jb.key + ji)}
                    <th
                      colspan={jb.items.length}
                      class="px-2 py-1 text-center {ji === 0 ? 'border-l hair' : 'border-l border-base-300/60'}"
                      title="{jb.items[0]?.job_description ?? ''}&#10;Re-run: pipeline.py job {jb.key} <community>"
                    >
                      <span class="whitespace-nowrap text-[0.62rem] font-medium text-base-content/62"
                        >{jobLabel(jb.key)}</span
                      >
                    </th>
                  {/each}
                {/each}
              </tr>
              <tr class="border-b hair bg-base-200/40 text-left">
                <th class="sticky left-0 z-10 bg-base-200/40 px-3 py-2"><span class="eyebrow">Community</span></th>
                {#each bands as b, bi (`${b.flow ?? 'manual'}-${bi}`)}
                  {#each b.jobs as jb, ji (jb.key + ji)}
                    {#each jb.items as st, i (st.name)}
                      <!-- The rate, under every column that has one — this is what the
                           grid is now sorted by, left to right, and an ordering you
                           cannot see the key for reads as no ordering at all.
                           Declared here rather than in the `<th>`: `{@const}` has to be
                           the immediate child of a block, and `svelte-check` passes it
                           either way — only `npm run build` catches the placement. -->
                      {@const rate = rateLabel(st)}
                      <th class="px-2 py-2 text-center {i === 0 ? (ji === 0 ? 'border-l hair' : 'border-l border-base-300/60') : ''}">
                        <span class="inline-flex items-center gap-1 whitespace-nowrap text-[0.7rem] font-medium text-base-content/78">
                          <!-- privacy-ok: a pipeline STAGE key, not a person -->
                          {stageLabel(st.name)}
                          <!-- privacy-ok: a pipeline STAGE key, not a person -->
                          {@render hint(st.description, runHint(st))}
                        </span>
                        {#if rate}
                          <div class="mt-px font-mono text-[0.5rem] uppercase tracking-wide text-base-content/45"
                               title={st.interval_secs
                                 ? 'the walk’s declared cadence; the adaptive band may move it between 5 and 15 minutes'
                                 : (CADENCE[st.cadence]?.note ?? '')}
                            >{rate}</div>
                        {/if}
                      </th>
                    {/each}
                  {/each}
                {/each}
              </tr>
            </thead>
            <tbody>
              {#each visibleCommunities as c (c.id)}
                <tr class="border-b hair last:border-0 hover:bg-base-200/40 {c.no_access ? 'opacity-55' : ''}">
                  <td class="sticky left-0 z-10 bg-base-100 px-3 py-2">
                    <div class="flex items-center gap-2.5">
                      {@render logo(c, 'h-7 w-7')}
                      <div class="min-w-0">
                        <a
                          href="https://www.skool.com/{c.slug}"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="block truncate font-medium hover:text-primary hover:underline"
                          title="Open {c.name} on Skool"><span class="redact">{c.name}</span></a
                        >
                        <div class="truncate font-mono text-[0.6rem] text-base-content/58">{c.slug}</div>
                      </div>
                    </div>
                  </td>
                  {#each bands as b, bi (`${b.flow ?? 'manual'}-${bi}`)}
                    {#each b.jobs as jb, ji (jb.key + ji)}
                      {#each jb.items as st, i (st.name)}
                        <td class="px-2 py-2 text-center {i === 0 ? (ji === 0 ? 'border-l hair' : 'border-l border-base-300/60') : ''}">
                          <!-- privacy-ok: a pipeline STAGE key, not a person -->
                          {@render cell(c.steps[st.name], st.name, c.no_access)}
                        </td>
                      {/each}
                    {/each}
                  {/each}
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
        <p class="mt-2 text-xs text-base-content/58">
          <!-- Four words carry almost all the confusion on this grid, and three of them
               mean "working as intended". Spelled out here because the alternative is a
               reader counting a wall of grey cells and concluding the fleet is broken. -->
          <span class="font-medium">never run</span> — no record yet. Under
          <span class="font-medium">on request</span> that is the normal state: no timer fires
          those steps, so they only ever fill in when somebody runs them.
          <span class="font-medium">not here</span> — the walk was never registered for this
          community. The membership tabs need a staff role and
          <span class="font-medium">Staff list</span> is the mirror image, so on a community we
          are an ordinary member of, most of the fetch columns are empty by design.
          <span class="font-medium">off</span> — the scheduler disabled that walk: blocked,
          no membership, or we were demoted out of the staff role.
          <span class="font-medium">no change</span> — the step looked and found nothing to do,
          which is healthy. Amber is the only colour that means go and look: a step past its
          own cadence, or a walk past the time the scheduler said it would next run it.
          Hover any cell for counts, mode and errors.
        </p>
      </section>

      <!-- Recent runs ------------------------------------------------------- -->
      <section class="mt-8 mb-4">
        <h2 class="eyebrow">Recent runs</h2>
        <div class="mt-2 overflow-x-auto rounded-box border hair bg-base-100">
          <table class="w-full min-w-[52rem] text-sm">
            <thead>
              <tr class="border-b hair bg-base-200/50 text-left">
                <th class="px-3 py-2"><span class="eyebrow">Started</span></th>
                <th class="px-2 py-2"><span class="eyebrow">Run</span></th>
                <th class="px-2 py-2"><span class="eyebrow">Community</span></th>
                <th class="px-2 py-2"><span class="eyebrow">Trigger</span></th>
                <th class="px-2 py-2"><span class="eyebrow">Result</span></th>
                <th class="px-2 py-2 text-right"><span class="eyebrow">Took</span></th>
                <th class="px-2 py-2"><span class="eyebrow">Summary</span></th>
              </tr>
            </thead>
            <tbody>
              {#if finished.length === 0}
                <tr><td colspan="7" class="px-3 py-4 text-center text-base-content/58">No runs recorded yet.</td></tr>
              {/if}
              {#each finished as j (j.id)}
                <tr class="border-b hair align-top last:border-0 hover:bg-base-200/40">
                  <td class="whitespace-nowrap px-3 py-2 text-xs text-base-content/70" title={j.started_at ?? ''}>{timeAgo(j.started_at, nowMs)}</td>
                  <td class="whitespace-nowrap px-2 py-2 font-medium">{j.type}</td>
                  <td class="px-2 py-2 font-mono text-xs text-base-content/70">
                    <span class="inline-flex items-center gap-1.5">
                      {#if j.slug}{@render logo(bySlug.get(j.slug), 'h-4 w-4')}{/if}
                      {j.slug ?? 'whole fleet'}
                    </span>
                  </td>
                  <td class="px-2 py-2 font-mono text-xs text-base-content/70">{j.trigger}</td>
                  <td class="px-2 py-2"><span class="{CHIP_BASE} {chipCls(j.status)}">{STATUS_WORD[j.status] ?? j.status}</span></td>
                  <td class="whitespace-nowrap px-2 py-2 text-right font-mono text-xs tabular-nums text-base-content/70">{duration(j.started_at, j.finished_at)}</td>
                  <!-- privacy-ok: a pipeline JOB's status line ("14 posts, 3 skipped"), not a member's words -->
                  <td class="px-2 py-2 text-xs text-base-content/70">{j.summary ?? ''}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>
    </PageContainer>
  {/if}
</AdminShell>
